import React from "react";
import { Box, Typography, Button, Container, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { keyframes } from "@emotion/react";

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const glow = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const PageNotFound = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
        },
      }}
    >
      {/* Floating Elements */}
      <Box
        sx={{
          position: "absolute",
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          top: "20%",
          left: "10%",
          animation: `${float} 6s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
          bottom: "20%",
          right: "10%",
          animation: `${float} 8s ease-in-out infinite`,
        }}
      />

      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: "center",
            color: "white",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Main 404 Animation */}
          <Box
            sx={{
              fontSize: { xs: "8rem", md: "12rem" },
              fontWeight: "bold",
              background: "linear-gradient(45deg, #fff 30%, #f0f0f0 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              textShadow: "0 0 30px rgba(255,255,255,0.5)",
              animation: `${glow} 2s ease-in-out infinite`,
              lineHeight: 1,
              mb: 2,
            }}
          >
            404
          </Box>

          {/* Animated Icon */}
          <Box
            sx={{
              fontSize: "4rem",
              mb: 3,
              animation: `${float} 3s ease-in-out infinite`,
            }}
          >
            🚀
          </Box>

          {/* Main Message */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 2,
              background: "linear-gradient(45deg, #fff 30%, #e0e0e0 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Oops! Lost in Space?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 4,
              opacity: 0.9,
              maxWidth: "500px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            The page you're looking for seems to have drifted off into the
            cosmic void. Don't worry, even astronauts get lost sometimes!
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
              sx={{
                background: "linear-gradient(45deg, #fff 30%, #f0f0f0 90%)",
                color: theme.palette.primary.main,
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                borderRadius: 3,
                boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 35px rgba(0,0,0,0.3)",
                  background:
                    "linear-gradient(45deg, #f8f8f8 30%, #e8e8e8 90%)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Back to Home
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: "white",
                color: "white",
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                borderRadius: 3,
                borderWidth: 2,
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Go Back
            </Button>
          </Box>

          {/* Quick Links */}
          <Box
            sx={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              p: 3,
              maxWidth: "400px",
              mx: "auto",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <SearchIcon />
              Quick Navigation
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/dashboard")}
                sx={{ color: "white", textDecoration: "underline" }}
              >
                Dashboard
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/profile")}
                sx={{ color: "white", textDecoration: "underline" }}
              >
                Profile
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/analytics")}
                sx={{ color: "white", textDecoration: "underline" }}
              >
                Analytics
              </Button>
            </Box>
          </Box>

          {/* Fun Facts */}
          <Box
            sx={{
              mt: 4,
              p: 2,
              background: "rgba(255,255,255,0.05)",
              borderRadius: 2,
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", opacity: 0.8 }}
            >
              💡 Did you know? The 404 error is named after Room 404 at CERN
              where the original web servers were located!
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PageNotFound;
